<template>
  <div
    class="wt-text-style"
    :class="{ 'edit-disabled': !isOwner, expanded: expanded }"
    :title="ownerName"
  >
    <div v-if="!editing" class="title" @click="toggleStyle">
      <span
        class="wticon"
        :class="[shared ? 'wticon-shared' : 'wticon-private']"
      />
      <span class="title-text" v-text="styleObject.name" />
      <span
        v-show="!editing"
        class="wt-toggle"
        :class="{ 'wt-expanded': expanded }"
        >&#9664;</span
      >
    </div>
    <div v-else class="wt-editing-style title" @click="toggleInfo">
      <div>
        <span class="wticon wticon-private" />
        <span class="wt-edit-text-style-checkbox">
          <input
            :id="'chkStyleShared' + index"
            type="checkbox"
            v-model="shared"
            @change="handleUpdate"
            :disabled="!isOwner"
          />
          <label :for="'chkStyleShared' + index" />
        </span>
        <span class="wticon wticon-shared" />
        <input
          type="text"
          name="style-name"
          v-model="name"
          :disabled="!isOwner"
          @blur="updateName"
          @keypress.enter="updateName"
        />
        <span
          v-show="editing"
          class="wticon wticon-trash2 js-delete-style"
          @click="handleDelete"
        />
      </div>
    </div>

    <div
      v-show="expanded && editing && mode === null"
      class="wt-info"
      v-text="ownerName"
    />

    <div
      v-if="expanded && editing && mode === 'deleting'"
      class="wt-confirmation"
    >
      Are you sure you want to delete this style?
      <div v-if="initialShared">
        Shared style will be deleted from all users.
      </div>
      <div class="wt-yes-no">
        <span class="wt-no" @click="cancelDelete">No</span>
        <span class="wt-yes" @click="remove">Yes</span>
      </div>
    </div>

    <div
      v-if="
        expanded && editing && mode === 'updating' && shared !== initialShared
      "
      class="wt-confirmation"
    >
      <div v-if="!shared">
        Style will be available only to you. Do you want to continue?
      </div>
      <div v-else>
        Style will be shared with your entire team. Do you want to continue?
      </div>
      <div class="wt-yes-no">
        <span class="wt-no" @click="cancelUpdate">No</span>
        <span class="wt-yes" @click="update">Yes</span>
      </div>
    </div>

    <div class="actions" v-show="expanded && !editing">
      <span>Include:</span>
      <div class="wt-checkbox-sm">
        <div></div>
        <label>
          <input
            type="checkbox"
            :checked="applyBoxDesign"
            @change="handleBoxToggle"
          />
          <span>Box design</span>
        </label>
      </div>
      <div class="wt-checkbox-sm">
        <div></div>
        <label>
          <input
            type="checkbox"
            :checked="applyPosition"
            @change="handlePositionToggle"
          />
          <span>Position</span>
        </label>
      </div>
    </div>

    <div
      v-if="styleObject.subStyles.length && expanded && !editing"
      class="sub-styles wt-text-overlay"
    >
      <div>
        <div
          :style="{
            background: emptyRowsSubStyleBg,
            height: '5px',
            marginLeft: '29px',
          }"
        />
      </div>
      <sub-style
        v-for="(sub, index) in styleObject.subStyles"
        :key="index"
        :bold="sub.bold"
        :color="sub.color"
        :bgColor="subStylesBG"
        :isGradient="isGradient"
        :italic="sub.italic"
        :font-name="sub.fontName"
        :class="{ active: index === activeSubStyle }"
        @apply-sub-style="onApplySubStyle"
        :index="index"
      />
      <div>
        <div
          :style="{
            background: emptyRowsSubStyleBg,
            height: '5px',
            marginLeft: '29px',
          }"
        />
      </div>
    </div>
  </div>
</template>

<script>
/** (GW)
 * Asset Editor's text overlay stage Vue (stage bar)
 */
import app from 'app';
import _ from 'lodash-extended';
import subStyle from 'AssetEditor/functionality/TextOverlay/SubStyle.vue';
import bus from 'v/AssetEditor/AssetEditorEventBus';
import Analytics from 'c/WtAnalytics';
import { mapMutations } from 'vuex';

const track = _.debounce(function (e, obj) {
  Analytics.trigger('analytics:textOverlay:' + e, obj);
}, 500);

export default {
  components: {
    'sub-style': subStyle,
  },

  props: ['style-object', 'editing', 'expanded', 'index', 'programmer'],

  data: function () {
    return {
      mode: this.styleObject.mode,
      initialShared: this.styleObject.privacy !== app.TEXT_STYLES.PRIVATE,
      shared: this.styleObject.privacy !== app.TEXT_STYLES.PRIVATE,
      name: this.styleObject.name,
      activeSubStyle: 0,
    };
  },

  computed: {
    applyBoxDesign: function () {
      return this.styleObject.box.applyCheckbox;
    },

    applyPosition: function () {
      return this.styleObject.position.applyCheckbox;
    },

    isOwner: function () {
      return this.styleObject.ownerId === tk.context.user.employeeId;
    },

    subStylesBG: function () {
      if (this.applyBoxDesign) {
        if (this.styleObject.box.bgColor) {
          return this.styleObject.box.bgColor;
        } else if (this.styleObject.box.gradient) {
          return app.namespace.Util.gradientModelToCss(
            this.styleObject.box.gradient
          );
        } else {
          return 'transparent';
        }
      } else {
        return 'transparent';
      }
    },

    isGradient: function () {
      return !!this.styleObject.box.gradient;
    },

    emptyRowsSubStyleBg: function () {
      return this.applyBoxDesign ? this.styleObject.box.bgColor : 'transparent';
    },

    ownerName: function () {
      return (
        'Created by ' +
        (this.styleObject.createdByCSM ? 'Wochit' : this.styleObject.ownerName)
      );
    },
  },

  methods: {
    ...mapMutations('assetEditor', [
      'deleteTextStyle',
      'updateTextStyle',
      'updateApplyBoxDesign',
      'updateApplyPosition',
    ]),

    onApplySubStyle: function (props, index) {
      this.activeSubStyle = index;
      bus.$emit('apply-sub-style', props, this.index);
    },

    toggleStyle: function () {
      if (this.editing) {
        return;
      }
      this.mode = 'applying';
      this.$emit('expanded', this.styleObject.id);
      if (!this.expanded) {
        this.$emit('apply-style', this.styleObject.id, 0, this.$el);
        this.activeSubStyle = 0;
        track('applyStyle', {
          box: this.styleObject.box.apply,
          position: this.styleObject.position.apply,
        });
      }
    },

    scrollToView: function () {
      setTimeout(() => {
        this.$el.scrollIntoView();
      }, 200);
    },

    toggleInfo: function () {
      if (!this.isOwner) {
        this.mode = null;
        this.$emit('expanded', this.styleObject.id);
        this.scrollToView();
      }
    },

    handleDelete: function (e) {
      if (this.isOwner) {
        if (e.shiftKey) {
          this.remove();
        } else {
          const needCollapse = this.mode === 'deleting' && this.expanded;
          this.mode = 'deleting';
          this.shared = this.initialShared;
          if (this.isOwner && (!this.expanded || needCollapse)) {
            this.$emit('expanded', this.styleObject.id);
            this.scrollToView();
          }
        }
      }
    },

    handleUpdate: function () {
      if (this.isOwner) {
        this.mode = 'updating';
        if (this.isOwner && !this.expanded) {
          this.$emit('expanded', this.styleObject.id);
          this.scrollToView();
        }
      }
    },

    remove: function () {
      const id = this.styleObject.id;
      if (this.isOwner) {
        track('textStyleDeleted');
      }
      this.deleteTextStyle(id);
      app.api.textStyles.delete(id).done(() => {
        // TODO error recovery
      });
    },

    updateName: function (e) {
      if (this.isOwner) {
        const o = {
          privacy: this.initialShared
            ? app.TEXT_STYLES.SHARED
            : app.TEXT_STYLES.PRIVATE,
          programmerId: this.programmer,
          name: this.name,
        };
        app.api.textStyles.put(this.styleObject.id, o);
        o.id = this.styleObject.id;
        this.updateTextStyle(o);
        track('textStyleEdited', { updatedField: 'name' });
        e.target.blur();
      }
    },

    update: function () {
      if (this.isOwner) {
        const o = {
          id: this.styleObject.id,
          privacy: this.shared
            ? app.TEXT_STYLES.SHARED
            : app.TEXT_STYLES.PRIVATE,
          programmerId: this.programmer,
          name: this.name,
        };
        this.mode = null;
        this.initialShared = this.shared;
        app.api.textStyles.put(this.styleObject.id, o);
        track('textStyleEdited', { updatedField: 'privacy' });
        this.updateTextStyle(o);
        this.$emit('expanded', this.styleObject.id);
      }
    },

    cancelUpdate: function () {
      this.mode = null;
      this.shared = this.initialShared;
      this.$emit('collapse');
    },

    cancelDelete: function () {
      this.mode = null;
      this.$emit('collapse');
    },

    handleBoxToggle: function (e) {
      this.updateApplyBoxDesign({
        id: this.styleObject.id,
        checked: e.target.checked,
      });
      bus.$emit(
        'toggle-box-design',
        e.target.checked ? this.styleObject : null
      );
    },

    handlePositionToggle: function (e) {
      this.updateApplyPosition({
        id: this.styleObject.id,
        checked: e.target.checked,
      });
      bus.$emit('toggle-position', e.target.checked ? this.styleObject : null);
    },

    setActiveSubStyle: function (style) {
      if (!this.expanded) {
        return;
      }
      this.activeSubStyle = _.findIndex(
        this.styleObject.subStyles,
        function (sub) {
          if (style.color !== sub.color || style.fontName !== sub.fontName) {
            return false;
          }
          if (style.isDefault) {
            return style.bold === sub.bold && style.italic === sub.italic;
          }
          return true;
        }
      );
    },
  },

  created: function () {
    bus.$on('to:subStyle', this.setActiveSubStyle);
  },

  beforeDestroy: function () {
    bus.$off('to:subStyle', this.setActiveSubStyle);
  },

  watch: {
    expanded: function (expanded) {
      if (!expanded) {
        this.updateApplyBoxDesign({
          id: this.styleObject.id,
          checked: this.styleObject.box.apply,
        });
        this.updateApplyPosition({
          id: this.styleObject.id,
          checked: this.styleObject.position.apply,
        });
        this.shared = this.initialShared;
      } else {
        this.scrollToView();
      }
    },
  },
};
</script>
